import {
  chaveDaCompra,
  descricaoDaCompra,
  linhaDe,
  podeAbater,
  podeSerAbatido,
} from '@domain/finance'
import type { Transaction } from '@domain/finance'
import { addDays, compareCivil, daysBetween } from '@shared/date/civil'
import type { CivilDate } from '@shared/date/civil'
import { addMonths, referenceMonthOf } from '@shared/date/reference-month'
import type { ReferenceMonth } from '@shared/date/reference-month'
import { sumCents } from '@shared/money/cents'
import type { Cents } from '@shared/money/cents'

/**
 * Quem pode entrar nas duas folhas do abatimento. Puro, para a tela só
 * desenhar. `Docs/05-Plano/Abatimento de gasto.md`, "Telas".
 *
 * **A janela é assimétrica de propósito:** quem devolve dinheiro devolve
 * DEPOIS da compra — o amigo manda o Pix na segunda, a mãe no mês seguinte.
 * Quinze dias antes cobre quem pagou adiantado.
 */
export const ANTES_DO_GASTO = 15
export const DEPOIS_DO_GASTO = 60

/** Os meses que uma janela de datas atravessa, em ordem. */
export function mesesDaJanela(inicio: CivilDate, fim: CivilDate): ReferenceMonth[] {
  const meses: ReferenceMonth[] = []
  const ultimo = referenceMonthOf(fim)
  for (let mes = referenceMonthOf(inicio); mes <= ultimo; mes = addMonths(mes, 1)) meses.push(mes)
  return meses
}

function unicas(transacoes: readonly Transaction[]): Transaction[] {
  return [...new Map(transacoes.map((t) => [t.id, t])).values()]
}

function naJanela(data: CivilDate, inicio: CivilDate, fim: CivilDate): boolean {
  return compareCivil(data, inicio) >= 0 && compareCivil(data, fim) <= 0
}

function distancia(de: CivilDate, ate: CivilDate): number {
  return Math.abs(daysBetween(de, ate))
}

/**
 * As entradas que podem abater `gasto`: vivas, de consumo, livres — ou já
 * ligadas a ESTA compra —, na janela, mais perto da data da compra primeiro.
 * Entrada ligada a outra compra nunca aparece: cada entrada abate um gasto só.
 */
export function entradasCandidatas(
  transacoes: readonly Transaction[],
  gasto: Transaction,
): Transaction[] {
  const chave = chaveDaCompra(gasto)
  const data = gasto.source.transactionDate
  const inicio = addDays(data, -ANTES_DO_GASTO)
  const fim = addDays(data, DEPOIS_DO_GASTO)
  return unicas(transacoes)
    .filter((t) => podeAbater(linhaDe(t)))
    .filter((t) => t.enrichment.offsetOf === null || t.enrichment.offsetOf.purchaseKey === chave)
    .filter((t) => naJanela(t.source.transactionDate, inicio, fim))
    .sort(
      (a, b) =>
        distancia(data, a.source.transactionDate) - distancia(data, b.source.transactionDate) ||
        a.id.localeCompare(b.id),
    )
}

export type CompraCandidata = {
  chave: string
  /** A linha que representa a compra — a primeira parcela, ou a própria. */
  transacao: Transaction
  descricao: string
  /** Em módulo: a soma das linhas vivas. */
  valorCents: Cents
  /** `null` à vista. */
  parcelas: number | null
  /** Quanto ainda cabe abater, pelo que o gatilho já gravou nas linhas. */
  cabeCents: Cents
}

/**
 * As compras que `entrada` pode abater: gastos vivos de consumo, de 60 dias
 * antes a 15 depois dela, agrupados pela compra — a parcelada aparece uma vez,
 * com o total.
 */
export function comprasCandidatas(
  transacoes: readonly Transaction[],
  entrada: Transaction,
): CompraCandidata[] {
  const data = entrada.source.transactionDate
  const inicio = addDays(data, -DEPOIS_DO_GASTO)
  const fim = addDays(data, ANTES_DO_GASTO)
  const porCompra = new Map<string, Transaction[]>()
  for (const t of unicas(transacoes)) {
    if (!podeSerAbatido(linhaDe(t))) continue
    if (!naJanela(t.source.transactionDate, inicio, fim)) continue
    const chave = chaveDaCompra(t)
    porCompra.set(chave, [...(porCompra.get(chave) ?? []), t])
  }
  return [...porCompra.entries()]
    .map(([chave, linhas]) => {
      const ordenadas = [...linhas].sort(
        (a, b) => (a.source.installment?.number ?? 0) - (b.source.installment?.number ?? 0),
      )
      const primeira = ordenadas[0] as Transaction
      const valorCents = sumCents(linhas.map((l) => -l.source.amountCents))
      const jaAbatido = sumCents(linhas.map((l) => l.enrichment.offset?.offsetCents ?? 0))
      return {
        chave,
        transacao: primeira,
        descricao: descricaoDaCompra(primeira),
        valorCents,
        parcelas: primeira.source.installment?.total ?? null,
        cabeCents: Math.max(0, valorCents - jaAbatido),
      }
    })
    .sort(
      (a, b) =>
        distancia(data, a.transacao.source.transactionDate) -
          distancia(data, b.transacao.source.transactionDate) || a.chave.localeCompare(b.chave),
    )
}

/**
 * A conta que a seção do gasto mostra, a partir das entradas que ela LEU — e
 * não do `offset` gravado, que espera o gatilho. É a mesma escolha do
 * detalhamento, que soma os itens que lê.
 */
export function resumoDoAbatimento(
  valorDaCompraCents: Cents,
  entradas: readonly Transaction[],
): { abatidoCents: Cents; ficaCents: Cents; excessoCents: Cents; vivas: Transaction[] } {
  const vivas = entradas.filter((e) => podeAbater(linhaDe(e)))
  const recebido = sumCents(vivas.map((e) => e.source.amountCents))
  const abatidoCents = Math.min(recebido, valorDaCompraCents)
  return {
    abatidoCents,
    ficaCents: valorDaCompraCents - abatidoCents,
    excessoCents: recebido - abatidoCents,
    vivas,
  }
}
